import React, { createContext, useContext } from 'react';
import { Button as MuiButton, ButtonProps as MuiButtonProps } from '@mui/material';
import { motion } from 'framer-motion';
import { colors } from '../../../styles/colors';

/**
 * Größe der Buttons in einem Bereich. Der Section-Kopf stellt 'compact' bereit: Seine Knöpfe
 * stehen neben einem Kacheltitel und sollen nicht höher wirken als er – überall gleich, ohne
 * dass jede Section ihre Knöpfe einzeln verkleinert.
 */
export const ButtonDensityContext = createContext<'regular' | 'compact'>('regular');

interface ButtonProps extends Omit<MuiButtonProps, 'variant'> {
  /**
   * `danger` – zerstörende Aktion (Löschen): umrandet und neutral wie `secondary`, beim Hover rot
   * (components/CLAUDE.md, „Zerstörende Aktionen“).
   */
  variant?: 'primary' | 'secondary' | 'danger';
  children: React.ReactNode;
}

/**
 * Custom Button Component
 * Moderne Button-Komponente mit Glassmorphism-Effekt durch framer-motion
 */
const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  children,
  sx = {},
  ...props
}) => {
  const isPrimary = variant === 'primary';
  const compact = useContext(ButtonDensityContext) === 'compact';

  const variantColors = isPrimary
    ? {
        bg: colors.button.primary.background,
        text: colors.button.primary.text,
        hoverBg: colors.button.primary.hover,
        hoverText: colors.button.primary.text,
        activeBg: colors.button.primary.background,
        activeText: colors.button.primary.text,
        border: 'none',
      }
    : variant === 'danger'
    ? {
        bg: 'transparent',
        text: colors.text.secondary,
        hoverBg: colors.status.error.light,
        hoverText: colors.status.error.main,
        activeBg: colors.status.error.light,
        activeText: colors.status.error.main,
        border: `1px solid ${colors.border.default}`,
        hoverBorder: colors.status.error.main,
      }
    : {
        bg: 'transparent',
        text: colors.button.secondary.text,
        hoverBg: colors.button.secondary.hover,
        hoverText: colors.button.secondary.textHover,
        activeBg: colors.button.secondary.active,
        activeText: colors.button.secondary.text,
        border: `1px solid ${colors.button.secondary.border}`,
      };

  const buttonStyles = {
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: 500,
    textTransform: 'none' as const,
    letterSpacing: '0.5px',
    padding: compact ? '3px 12px' : '5px 15px',
    ...(compact && {
      fontSize: '0.8125rem',
      '& .MuiButton-startIcon svg': { fontSize: '18px' },
    }),
    overflow: 'hidden',
    boxShadow: 'none',
    background: variantColors.bg,
    color: variantColors.text,
    border: variantColors.border,
    '&.MuiButton-root': { borderRadius: '8px' },
    '&.MuiButton-sizeLarge': { borderRadius: '8px' },
    '&:hover': {
      background: variantColors.hoverBg,
      backgroundColor: variantColors.hoverBg,
      color: variantColors.hoverText,
      ...('hoverBorder' in variantColors && { borderColor: variantColors.hoverBorder }),
      boxShadow: isPrimary ? `0 2px 8px ${colors.shadow.light}` : 'none',
    },
    '&:active': {
      background: variantColors.activeBg,
      backgroundColor: variantColors.activeBg,
      color: variantColors.activeText,
      boxShadow: 'none',
    },
  };

  // Framer Motion Variants für Animationen
  const buttonVariants = {
    initial: { scale: 1, y: 0 },
    hover: {
      scale: 1.01,
      y: -2,
      transition: { type: "spring" as const, stiffness: 300, damping: 15 }
    },
    tap: {
      scale: 0.99,
      y: -1,
      transition: { type: "spring" as const, stiffness: 300, damping: 15 }
    }
  };

  const iconVariants = {
    initial: { rotate: 0, scale: 1 },
    hover: {
      rotate: 45,
      scale: 1.05,
      transition: { type: "spring" as const, stiffness: 300, damping: 15 }
    }
  };

  return (
    <motion.div
      variants={buttonVariants}
      initial="initial"
      whileHover="hover"
      whileTap="tap"
      style={{ display: 'inline-block' }}
    >
      <MuiButton
        variant="contained"
        size="large"
        startIcon={props.startIcon ? (
          <motion.div
            variants={iconVariants}
            initial="initial"
            whileHover="hover"
          >
            {props.startIcon}
          </motion.div>
        ) : undefined}
        sx={{
          ...buttonStyles,
          ...sx,
        }}
        {...props}
      >
        {children}
      </MuiButton>
    </motion.div>
  );
};

export default Button;
